/**
 * API client for the R.U.S.H. Nexus app.
 * Game calls are POST /v1/app/rush/fn/<name> on the same origin.
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { networkLogger } from './networkLogger';
import { publicAssetUrl, rushFunctionPath } from './gameApi';
import type {
  CreateGameRequest,
  CreateGameResponse,
  JoinGameResponse,
  GameState,
  SubmitTurnRequest,
  SubmitTurnResponse,
  SignUpRequest,
  SignInRequest,
  AuthTokens,
  ListLobbiesResponse,
  PlayerProfile,
} from '@/types';

class ApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: '',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    // Request interceptor to add auth token and log requests
    this.client.interceptors.request.use(
      (config) => {
        if (this.accessToken) {
          config.headers.Authorization = `Bearer ${this.accessToken}`;
        }
        
        // Log request
        const requestId = networkLogger.logRequest({
          method: config.method?.toUpperCase() || 'GET',
          url: config.url || '',
          headers: config.headers as Record<string, string>,
          data: config.data,
        });
        
        // Store request ID for response logging
        (config as any).__requestId = requestId;
        (config as any).__requestStartTime = Date.now();
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling and logging
    this.client.interceptors.response.use(
      (response) => {
        // Log successful response
        const requestId = (response.config as any).__requestId;
        const requestStartTime = (response.config as any).__requestStartTime;
        const duration = requestStartTime ? Date.now() - requestStartTime : undefined;
        
        if (requestId) {
          networkLogger.logResponse(requestId, {
            status: response.status,
            statusText: response.statusText,
            data: response.data,
            headers: response.headers as Record<string, string>,
            duration,
          });
        }
        
        return response;
      },
      (error: AxiosError) => {
        // Log error response
        const requestId = (error.config as any)?.__requestId;
        const requestStartTime = (error.config as any)?.__requestStartTime;
        const duration = requestStartTime ? Date.now() - requestStartTime : undefined;
        
        if (requestId) {
          networkLogger.logError(requestId, {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            duration,
          });
        }
        // Handle authentication/authorization errors (401, 403)
        // BUT exclude auth endpoints (sign-in, sign-up, verify) as they return 401 for invalid credentials
        // Also exclude /leave endpoint as 403 might be a legitimate game error (player not in game)
        const url = error.config?.url || '';
        const isAuthEndpoint = url.includes('/auth/signin') || 
                               url.includes('/auth/signup') || 
                               url.includes('/auth/verify') ||
                               url.includes('/auth/resend');
        const isLeaveEndpoint = url.includes('/leave');
        
        if ((error.response?.status === 401 || error.response?.status === 403) && !isAuthEndpoint && !isLeaveEndpoint) {
          // Token expired, invalid, or insufficient permissions (only for protected endpoints)
          this.setAccessToken(null);
          
          // Clear stored tokens
          localStorage.removeItem('auth_tokens');
          localStorage.removeItem('auth_user');
          
          // Dispatch custom event for global error handler to catch
          const authErrorEvent = new CustomEvent('auth:session-expired', {
            detail: {
              status: error.response?.status,
              message: error.response?.status === 403 
                ? 'Your session has expired due to inactivity. Please sign in again.'
                : 'Your session has expired. Please sign in again.',
            },
          });
          window.dispatchEvent(authErrorEvent);
        }
        return Promise.reject(error);
      }
    );
  }

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  // Authentication Endpoints
  async signUp(data: SignUpRequest): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post('/auth/signup', data);
    return response.data;
  }
  
  async resendVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    // Note: We need to create a Lambda function for this, but for now
    // we can try signing up again - Cognito will resend the code if user exists
    // This is a workaround until we have a dedicated resend endpoint
    try {
      // Attempt to sign up again - if user exists, Cognito may resend code
      // This is not ideal but works as a temporary solution
      await this.client.post('/auth/signup', { 
        email, 
        password: 'TempPassword123!@#' // Dummy password - won't be used if user exists
      });
      return { success: true, message: 'Verification code resent' };
    } catch (error: any) {
      // If user exists, Cognito might still resend the code
      const errorMessage = error.response?.data?.message || '';
      if (errorMessage.includes('already exists') || errorMessage.includes('UsernameExistsException')) {
        // User exists - code might have been resent anyway
        // Return success since Cognito often resends codes even on this error
        return { success: true, message: 'If your account exists, a new code has been sent' };
      }
      // For other errors, still return success as Cognito may have resent
      return { success: true, message: 'Verification code request processed' };
    }
  }

  async signIn(data: SignInRequest): Promise<AuthTokens> {
    const response = await this.client.post('/auth/signin', data);
    return response.data;
  }

  async verifyEmail(email: string, code: string): Promise<{ success: boolean; message?: string }> {
    try {
      // Lambda expects 'verificationCode' not 'code'
      const response = await this.client.post('/auth/verify', { 
        email, 
        verificationCode: code 
      });
      return response.data;
    } catch (error: any) {
      // Return error in a format the UI can handle
      throw new Error(error.response?.data?.message || 'Verification failed');
    }
  }

  private async fn<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
    const response = await this.client.post(rushFunctionPath(name), body);
    return response.data as T;
  }

  // Game Management Endpoints
  async createGame(data: CreateGameRequest): Promise<CreateGameResponse> {
    return this.fn('create_game', data as unknown as Record<string, unknown>);
  }

  async getGameState(gameId: string): Promise<GameState> {
    return this.fn('get_game_state', { gameId });
  }

  async joinGame(gameId: string): Promise<JoinGameResponse> {
    return this.fn('join_game', { gameId });
  }

  async leaveGame(gameId: string): Promise<{ success: boolean }> {
    return this.fn('leave_game', { gameId });
  }

  async submitTurn(gameId: string, data: SubmitTurnRequest): Promise<SubmitTurnResponse> {
    return this.fn('submit_turn', { gameId, ...data });
  }

  async phaseComplete(body: {
    gameId: string;
    phase: string;
    etag?: string;
  }): Promise<{
    success: boolean;
    gameState?: GameState;
    allPlayersReady?: boolean;
    newPhase?: string;
    completedPlayers?: string[];
    totalPlayers?: number;
  }> {
    return this.fn('phase_complete', body);
  }

  async completeDevelopmentTurn(body: {
    gameId: string;
    plotId?: string;
    outfitType?: string;
    lounge?: boolean;
    place?: boolean;
    pickup?: boolean;
    runaway?: boolean;
    stand?: { q: number; r: number; s?: number };
  }): Promise<GameState & { loungeResult?: { won: boolean; amount: number; chance: number } }> {
    return this.fn('complete_development_turn', body);
  }

  async finishAuction(
    gameId: string,
    trades: Array<{ resource: string; buyerId: string; sellerId: string; price: number; quantity: number }>,
    etag?: string,
  ): Promise<{ success: boolean; gameState: GameState; currentTurn: number; phase: string }> {
    return this.fn('finish_auction', { gameId, trades, etag });
  }

  async auctionBid(gameId: string, bid: { role?: 'buyer' | 'seller'; targetPrice?: number }): Promise<{ success: boolean; gameState: GameState }> {
    return this.fn('auction_bid', { gameId, ...bid });
  }

  async selectAvatar(gameId: string, avatarId: string): Promise<{ success: boolean; gameState: GameState }> {
    return this.fn('select_avatar', { gameId, avatarId });
  }

  // Lobby Endpoints
  async listLobbies(params?: {
    status?: string;
    levelId?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListLobbiesResponse> {
    return this.fn('list_lobbies', {
      status: params?.status,
      levelId: params?.levelId,
      limit: params?.limit,
      offset: params?.offset,
    });
  }

  // Player Profile Endpoints
  async getPlayerProfile(playerId: string): Promise<PlayerProfile> {
    const response = await this.client.get(`/players/${playerId}/profile`);
    return response.data;
  }

  // Icon Endpoints
  async getIcon(iconName: string): Promise<string> {
    return publicAssetUrl(iconName);
  }

  // Store Endpoints
  async sellResources(gameId: string, resources: Record<string, number>): Promise<{
    success: boolean;
    earnedMoney: number;
    playerMoney: number;
    message: string;
  }> {
    return this.fn('sell_resources', { gameId, resources });
  }

  async getPlayerHistory(
    _playerId: string,
    _startDate: string,
    _endDate: string,
    _limit: number,
  ): Promise<{ history: [] }> {
    return { history: [] };
  }

  async deletePlayerHistory(_playerId: string): Promise<{ success: boolean }> {
    return { success: true };
  }

  async purchaseRover(gameId: string): Promise<{
    success: boolean;
    rover: { id: string; isOutfitted: boolean; outfitType: string | null };
    playerMoney: number;
    storeInventory: { availableRovers: number };
    message: string;
  }> {
    return this.fn('purchase_rover', { gameId });
  }

  async outfitRover(gameId: string, outfitType: string, roverId?: string): Promise<{
    success: boolean;
    rover: { id: string; isOutfitted: boolean; outfitType: string };
    playerMoney: number;
    availableRovers: number;
    storeInventory?: { availableRovers: number };
    message: string;
  }> {
    return this.fn('outfit_rover', { gameId, outfitType, roverId });
  }

  async sellRover(gameId: string, options?: { equipment?: boolean; outfitType?: string }): Promise<{
    success: boolean;
    price: number;
    playerMoney: number;
    soldEquipment?: string;
    mule?: { isOutfitted: boolean; outfitType: string | null };
    storeInventory: {
      availableRovers: number;
      sellPrice?: number;
      buybackPrice?: number;
    };
  }> {
    return this.fn('sell_rover', { gameId, ...(options || {}) });
  }
}

export const apiClient = new ApiClient();

